/**
 * Toast Helper Utility
 * Provides automatic toast type detection based on HTTP status codes and operation results
 */

export const ToastType = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info'
};

export const ToastIcon = {
    success: 'check_circle',
    error: 'error',
    warning: 'warning',
    info: 'info'
};

/**
 * Determine toast type from HTTP status code
 * @param {number|null} statusCode - HTTP status code
 * @returns {string} Toast type
 */
export function getToastTypeFromStatus(statusCode) {
    if (!statusCode) return ToastType.ERROR;
    
    if (statusCode >= 200 && statusCode < 300) {
        return ToastType.SUCCESS;
    }
    
    if (statusCode >= 400 && statusCode < 500) {
        return ToastType.WARNING;
    }
    
    if (statusCode >= 500) {
        return ToastType.ERROR;
    }
    
    return ToastType.INFO;
}

/**
 * Determine if a response indicates success
 * @param {object} response - Axios response object
 * @returns {boolean}
 */
export function isSuccessResponse(response) {
    if (!response || !response.status) return false;
    return response.status >= 200 && response.status < 300;
}

/**
 * Get appropriate toast type from axios error
 * @param {object} error - Axios error object
 * @returns {string} Toast type
 */
export function getToastTypeFromError(error) {
    if (!error || !error.response) {
        return ToastType.ERROR;
    }
    
    const status = error.response.status;
    
    if (status === 401) {
        return ToastType.WARNING;
    }
    
    if (status === 403) {
        return ToastType.WARNING;
    }
    
    if (status === 404) {
        return ToastType.INFO;
    }
    
    if (status >= 400 && status < 500) {
        return ToastType.WARNING;
    }
    
    if (status >= 500) {
        return ToastType.ERROR;
    }
    
    return ToastType.ERROR;
}

/**
 * Extract error message from various error formats
 * @param {object} error - Error object (axios or standard)
 * @param {string} fallback - Fallback message
 * @returns {string} Error message
 */
export function extractErrorMessage(error, fallback = 'An error occurred. Please try again.') {
    if (!error) return fallback;
    
    if (error.response?.data?.message) {
        return error.response.data.message;
    }
    
    if (error.response?.data?.error) {
        return error.response.data.error;
    }
    
    if (error.message) {
        return error.message;
    }
    
    return fallback;
}

/**
 * Format success message from response
 * @param {object} response - Axios response object
 * @param {string} defaultMessage - Default success message
 * @returns {string} Success message
 */
export function extractSuccessMessage(response, defaultMessage = 'Operation completed successfully!') {
    if (!response) return defaultMessage;
    
    if (response.data?.message) {
        return response.data.message;
    }
    
    if (response.data?.success) {
        return response.data.success;
    }
    
    return defaultMessage;
}

/**
 * Create toast payload from error
 * @param {object} error - Error object
 * @returns {object} Toast payload { message, type }
 */
export function createErrorToast(error) {
    return {
        message: extractErrorMessage(error),
        type: getToastTypeFromError(error)
    };
}

/**
 * Create toast payload from success response
 * @param {object} response - Success response
 * @param {string} customMessage - Optional custom message
 * @returns {object} Toast payload { message, type }
 */
export function createSuccessToast(response, customMessage = null) {
    return {
        message: customMessage || extractSuccessMessage(response),
        type: ToastType.SUCCESS
    };
}

/**
 * Auto-detect toast type and create payload from axios response or error
 * @param {object} payload - Either response or error object
 * @param {boolean} isError - Whether this is an error
 * @param {string} customMessage - Optional custom message
 * @returns {object} Toast payload { message, type }
 */
export function autoCreateToast(payload, isError = false, customMessage = null) {
    if (isError) {
        const errorToast = createErrorToast(payload);
        if (customMessage) {
            errorToast.message = customMessage;
        }
        return errorToast;
    }
    
    return createSuccessToast(payload, customMessage);
}
